import { nextTick, onUnmounted, watch, type Ref } from "vue";
import { type RouteLocationNormalizedLoaded, useRoute } from "vue-router";

/**
 * Composable that handles deep-link scroll + flash for section anchors in
 * MasterProfileLayout (spec §3.3–§3.4, improvement-119).
 *
 * Watches `status` and `route.hash`. When status==='ready' and the hash
 * matches a known sectionId, it scrolls the section into view, focuses
 * its heading, and adds a transient flash class (~800 ms). Reduced-motion
 * aware: instant scroll + no animation when `prefers-reduced-motion: reduce`.
 */
export function useSectionDeepLink(
  status: Ref<string>,
  sectionIds: readonly string[],
) {
  // useRoute() returns undefined at runtime when no router plugin is provided
  // (e.g. in legacy test environments). Cast explicitly so we can guard below.
  const route = useRoute() as RouteLocationNormalizedLoaded | undefined;

  let flashTimer: ReturnType<typeof setTimeout> | undefined;
  // Consumed flag: prevents re-scroll on retry (error→loading→ready) without
  // a new navigation intent. Reset when route.hash changes.
  let consumed = false;

  function reducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  // rm is computed once per nextTick call to avoid two matchMedia queries in
  // the same microtask (Bug 8).
  function flash(el: HTMLElement, rm: boolean): void {
    if (rm) return;
    // Cancel any in-flight removal timer before starting a new animation
    // so rapid re-entry (< 800ms) does not corrupt the flash state (Bug 3).
    clearTimeout(flashTimer);
    el.classList.add("section-flash");
    flashTimer = setTimeout(() => el.classList.remove("section-flash"), 800);
  }

  function run(): void {
    if (!route || status.value !== "ready") return;

    const hash = route.hash;
    if (!hash) return;

    const key = hash.slice(1);
    if (!sectionIds.includes(key)) return;

    // Guard: only scroll once per navigation intent. Prevents a retry cycle
    // (error→loading→ready) from re-scrolling the section (Bug 2).
    if (consumed) return;
    consumed = true;

    void nextTick(() => {
      const el = document.getElementById(key);
      if (!el) return;

      // Single reducedMotion query per tick — passed into flash() (Bug 8).
      const rm = reducedMotion();

      el.scrollIntoView({
        behavior: rm ? "auto" : "smooth",
        block: "start",
      });

      const heading =
        el.querySelector<HTMLElement>("[data-section-heading]") ?? el;
      heading.focus({ preventScroll: true });

      flash(el, rm);
    });
  }

  // Cancel any pending flash timer on unmount to avoid removing the class
  // after the component is gone (Bug 5).
  onUnmounted(() => clearTimeout(flashTimer));

  // No router provided (e.g. test environment without router plugin) → no-op.
  if (!route) return;

  // New hash = new navigation intent → allow the next ready→scroll (Bug 2).
  watch(() => route.hash, () => {
    consumed = false;
  });

  // immediate: true ensures scroll fires when status is already 'ready' at
  // mount (e.g. returning visitor with cached data) — Bug 1.
  watch([() => status.value, () => route.hash], run, { immediate: true });
}
